import React from "react";
import { Box, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { normalizeLocationId } from "../lobby/lobbyPageUtils";
import {
    MOBILE_NAV_MQ,
    SETTINGS_CONTENT_COLUMN_SX,
    SETTINGS_SECTION_BODY_SX,
    SETTINGS_SECTION_DESC_SX,
    SETTINGS_SECTION_HEADER_SX,
    SETTINGS_SECTION_PAD_SX,
    SETTINGS_SECTION_TITLE_SX,
} from "./lobbySettingsStyles";

function formatLocationIdFallback(locationId) {
    return String(locationId || "")
        .trim()
        .split("-")
        .filter(Boolean)
        .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(" ");
}

export default function ServerLocationsTab({
    selectedServerLocations = [],
    serverLocations,
    locationEditingDisabled = false,
    canEditLobbySettings = false,
    currentLobbyCode = null,
    canRefineSelections = true,
    onServerLocationClicked,
    onLeaderServerLocationUpdate,
}) {
    const displayLocations = Array.isArray(serverLocations)
        ? serverLocations.map((location, index) => {
              const normalizedLocationId = normalizeLocationId(
                  location?.id ?? location?.awsRegion,
              );
              const displayName =
                  location?.displayName ||
                  location?.name ||
                  formatLocationIdFallback(normalizedLocationId) ||
                  "Unknown region";
              const country =
                  location?.country ||
                  location?.subtitle ||
                  location?.metaDescription ||
                  "";
              return {
                  id: normalizedLocationId,
                  uiKey: `${normalizedLocationId || "unknown"}-${index}`,
                  name: displayName,
                  country,
                  regionCode: normalizedLocationId,
                  available:
                      location?.enabled !== false && Boolean(normalizedLocationId),
                  flagImageUrl: location?.flagImageUrl || "",
              };
          })
        : [];

    const normalizedSelectedLocationIds = selectedServerLocations
        .map((locationId) => normalizeLocationId(locationId))
        .filter(Boolean);

    const handleServerLocationClick = (locationId) => {
        if (locationEditingDisabled) {
            return;
        }
        const normalizedLocationId = normalizeLocationId(locationId);
        if (
            canEditLobbySettings &&
            currentLobbyCode &&
            onLeaderServerLocationUpdate
        ) {
            onLeaderServerLocationUpdate(normalizedLocationId);
        } else if (onServerLocationClicked) {
            onServerLocationClicked(normalizedLocationId);
        }
    };

    return (
        <Box sx={SETTINGS_SECTION_PAD_SX}>
            <Box sx={SETTINGS_CONTENT_COLUMN_SX}>
                <Box sx={SETTINGS_SECTION_HEADER_SX}>
                    <Typography sx={SETTINGS_SECTION_TITLE_SX}>
                        Server locations
                    </Typography>
                    <Typography sx={SETTINGS_SECTION_DESC_SX}>
                        {!canRefineSelections
                            ? "Free keeps all enabled server locations active."
                            : "Choose the regions your lobby is willing to play on."}
                    </Typography>
                </Box>

                <Box sx={SETTINGS_SECTION_BODY_SX}>
                    {displayLocations.length === 0 ? (
                        <Typography
                            sx={{
                                fontSize: "0.85rem",
                                color: colors.text.secondary,
                            }}
                        >
                            No enabled server locations are available yet.
                        </Typography>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {displayLocations.map((location) => {
                                    const isAvailable = location.available;
                                    const isSelected =
                                        normalizedSelectedLocationIds.includes(
                                            location.id,
                                        );
                                    const isClickable =
                                        isAvailable && !locationEditingDisabled;
                                    const meta = [location.country, location.regionCode]
                                        .filter(Boolean)
                                        .join(" · ");

                                    return (
                                        <Box
                                            key={location.uiKey}
                                            onClick={
                                                isClickable
                                                    ? () =>
                                                          handleServerLocationClick(
                                                              location.id,
                                                          )
                                                    : undefined
                                            }
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "0.85rem",
                                                minHeight: "2.85rem",
                                                py: "0.5rem",
                                                px: "0.55rem",
                                                mx: "-0.35rem",
                                                borderRadius: "4px",
                                                border: "1px solid transparent",
                                                boxSizing: "border-box",
                                                cursor: isClickable
                                                    ? "pointer"
                                                    : "default",
                                                opacity: isAvailable ? 1 : 0.4,
                                                pointerEvents:
                                                    locationEditingDisabled
                                                        ? "none"
                                                        : "auto",
                                                transition:
                                                    "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                                                "&:hover": isClickable
                                                    ? {
                                                          backgroundColor:
                                                              "rgba(255,255,255,0.07)",
                                                          borderColor:
                                                              "rgba(255,255,255,0.06)",
                                                      }
                                                    : undefined,
                                                [MOBILE_NAV_MQ]: {
                                                    minHeight: "2.55rem",
                                                    gap: "0.65rem",
                                                    py: "0.4rem",
                                                    px: "0.45rem",
                                                    mx: "-0.25rem",
                                                },
                                            }}
                                        >
                                            {location.flagImageUrl ? (
                                                <Box
                                                    component="img"
                                                    src={location.flagImageUrl}
                                                    alt=""
                                                    sx={{
                                                        width: 22,
                                                        height: 16,
                                                        borderRadius: "2px",
                                                        objectFit: "cover",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        width: 22,
                                                        height: 16,
                                                        borderRadius: "2px",
                                                        flexShrink: 0,
                                                        backgroundColor:
                                                            "rgba(255,255,255,0.08)",
                                                    }}
                                                />
                                            )}
                                            <Typography
                                                sx={{
                                                    flex: "1 1 auto",
                                                    minWidth: 0,
                                                    color: colors.text.primary,
                                                    fontSize: "0.92rem",
                                                    fontWeight: 700,
                                                    letterSpacing: "0.01em",
                                                    lineHeight: 1.2,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    [MOBILE_NAV_MQ]: {
                                                        fontSize: "0.84rem",
                                                    },
                                                }}
                                            >
                                                {location.name}
                                            </Typography>
                                            {meta ? (
                                                <Typography
                                                    sx={{
                                                        flex: "0 1 auto",
                                                        maxWidth: "42%",
                                                        color: "rgba(255,255,255,0.42)",
                                                        fontSize: "0.72rem",
                                                        fontWeight: 500,
                                                        letterSpacing: "0.01em",
                                                        lineHeight: 1.2,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        textAlign: "right",
                                                        [MOBILE_NAV_MQ]: {
                                                            fontSize: "0.64rem",
                                                            maxWidth: "36%",
                                                        },
                                                    }}
                                                >
                                                    {meta}
                                                </Typography>
                                            ) : null}
                                            <Box
                                                sx={{
                                                    width: "18px",
                                                    height: "18px",
                                                    flexShrink: 0,
                                                    borderRadius: "4px",
                                                    boxSizing: "border-box",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: isSelected
                                                        ? colors.primary.main
                                                        : "transparent",
                                                    border: isSelected
                                                        ? "none"
                                                        : "1px solid rgba(255,255,255,0.18)",
                                                }}
                                            >
                                                {isSelected ? (
                                                    <Check
                                                        sx={{
                                                            fontSize: "0.78rem",
                                                            color: colors.background.dark,
                                                        }}
                                                    />
                                                ) : null}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
